import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import DownloadTemplateModal from 'components/molecules/PricingSimulator/Modals/DownloadTemplateModal';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { PricingSimulatorWrapperContext } from 'contexts/PricingSimulatorWrapperContext';
import { pricingSimulatorUrls } from 'config/urls';
import { getLenseConfigParser } from 'parser/pricingSimulator/lenseConfigParser';
import { getAuthUserMail } from 'utils/helperMethods';
import type { Project, Scenario } from 'types/pricingSimulator';

type DownloadTemplateModalBridgeProps = {
    open: boolean;
    onClose: () => void;
    scenarioId: number;
    projectId: number;
    projectName: string;
    scenarioName: string;
    createdBy?: string;
};

const PricingSimulatorContextBridge: FC<DownloadTemplateModalBridgeProps> = ({
    open,
    onClose,
    scenarioId,
    projectId,
    projectName,
    scenarioName,
    createdBy = '',
}) => {
    const parentContext = useContext(pricingSimulatorDataContext);
    const loggedInUser = getAuthUserMail() ?? '';
    const [selectedScenarioInfo, setSelectedScenarioInfo] = useState<Scenario | null>(null);
    const [selectedProjectInfo, setSelectedProjectInfo] = useState<Project | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedScenarioInfo(null);
            setSelectedProjectInfo(null);
            return;
        }

        setSelectedScenarioInfo({
            scenarioId,
            projectId,
            scenarioName,
            createdBy,
            updatedAt: '',
            status: 'SETUP',
        });
        setSelectedProjectInfo({
            projectId,
            projectName,
            createdBy,
            lastUpdated: '',
            dataSource: '',
            dim1Filter: null,
            dim2Filter: null,
            status: 'SETUP',
            simulationPeriod: '',
            priceEffectiveDate: '',
        });
    }, [createdBy, open, projectId, projectName, scenarioId, scenarioName]);

    const { data: lenseConfig, isFetching: lenseConfigLoading } = useQuery({
        queryKey: ['ps-v3-download-template-lense-config', scenarioId, loggedInUser],
        enabled: open && scenarioId > 0 && Boolean(loggedInUser),
        queryFn: async () => {
            const response = await axios.get(pricingSimulatorUrls.getLenseConfigUrl, {
                params: {
                    scenario_id: scenarioId,
                    user_email: loggedInUser,
                },
            });
            return getLenseConfigParser(response.data);
        },
    });

    useEffect(() => {
        if (!open || !lenseConfig) {
            return;
        }

        setSelectedScenarioInfo((current) =>
            current
                ? {
                      ...current,
                      lenseConfig,
                  }
                : current,
        );
    }, [lenseConfig, open]);

    const contextValue = useMemo(
        () => ({
            ...parentContext,
            projectId,
            scenarioId,
            selectedScenarioInfo,
            selectedProjectInfo,
            lenseConfigLoading,
            loggedInUser,
        }),
        [lenseConfigLoading, loggedInUser, parentContext, projectId, scenarioId, selectedProjectInfo, selectedScenarioInfo],
    );

    return (
        <pricingSimulatorDataContext.Provider value={contextValue}>
            <DownloadTemplateModal open={open} onCancel={onClose} onSubmitFinish={onClose} />
        </pricingSimulatorDataContext.Provider>
    );
};

const DownloadTemplateModalBridge: FC<DownloadTemplateModalBridgeProps> = (props) => (
    <PricingSimulatorWrapperContext>
        <PricingSimulatorContextBridge {...props} />
    </PricingSimulatorWrapperContext>
);

export default DownloadTemplateModalBridge;
